import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, Loading } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { useRegisterProviderClientMutation } from "@/redux/apis/auth/authApi";
import { RegisterProviderClientParams } from "@/redux/apis/auth/types";
import { addToast } from "@/utils";
import { CompanyField } from "./fields/CompanyField";
import { ContactNumberField } from "./fields/ContactNumberField";
import { EmailField } from "./fields/EmailField";
import { FirstNameField } from "./fields/FirstNameField";
import { HowDidTheClientHearAboutUsField } from "./fields/HowDidTheClientHearAboutUsField";
import { LastNameField } from "./fields/LastNameField";
import { addClientFormSchema } from "./fixtures";
import { AddClientFormData } from "./types";

export const AddClientForm = () => {
  const navigate = useNavigate();
  const operator = useOperator();
  const [registerProviderClient, { isLoading }] = useRegisterProviderClientMutation();
  const methods = useForm<AddClientFormData>({
    resolver: zodResolver(addClientFormSchema),
    defaultValues: {
      howDidTheClientHearAboutUs: "",
    },
  });
  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const handleCancelClick = () => {
    navigate("../");
  };

  const onSubmit = handleSubmit((data) => {
    if (!operator) return;

    const clientData: RegisterProviderClientParams = {
      providerId: operator.id,
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      mobile: data.contactNumber || "",
      provider_code: operator.providerCode,
      registered_device: "browser",
      hear_about_us: data.howDidTheClientHearAboutUs,
      company_uuid: data.company || undefined,
      send_login_instructions: true,
      password: undefined,
      account_metadata: {
        is_register_complete: true,
        provider_code: operator.providerCode,
        type: "passenger",
      },
    };

    registerProviderClient(clientData)
      .unwrap()
      .then(() => {
        addToast("success", "Successfully added new client");
        navigate("../");
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <div className="mt-8 grid animate-enter grid-cols-3 gap-x-2.5 gap-y-3 lg:gap-x-5 lg:gap-y-6">
          <FirstNameField className="col-span-3 lg:col-span-1" />
          <LastNameField className="col-span-3 lg:col-span-1" />
          <CompanyField className="col-span-3 lg:col-span-1" />
          <EmailField className="col-span-3 lg:col-span-1" />
          <ContactNumberField className="col-span-3 lg:col-span-1" />
          <HowDidTheClientHearAboutUsField className="col-span-3 lg:col-span-1" />
        </div>
        <div className="mb-4 mt-10 flex justify-end gap-x-2">
          <Button variant="secondary" onClick={handleCancelClick} size="lg">
            Cancel
          </Button>
          <div className="relative">
            {isLoading && <Loading />}
            <Button variant="primary" type="submit" disabled={!isDirty} size="lg">
              Add Client
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
